import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { CommonFunction } from 'src/app/helpers/commonFunction';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { MeetingMasterService } from 'src/app/services/meeting-master.service';

export interface AgendaVoteRow {
  agenda: any;
  summary: {
    approveCount: number;
    rejectCount: number;
    totalVotes: number;
    approveVoters: any[];
    rejectVoters: any[];
  } | null;
  errorMessage: string | null;
}

export interface MinuteVoteRow {
  minuteType: 'DRAFT' | 'FINAL';
  label: string;
  summary: AgendaVoteRow['summary'];
  errorMessage: string | null;
}

@Component({
  selector: 'app-meeting-voting-results-tab',
  templateUrl: './meeting-voting-results-tab.component.html',
  styleUrls: ['./meeting-voting-results-tab.component.css'],
})
export class MeetingVotingResultsTabComponent implements OnChanges {
  /** Meeting Mongo _id */
  @Input() meetingId: string;
  /** Agenda rows from meeting (same source as Agenda tab) */
  @Input() agendas: any[] = [];
  /** Notice / draft / final / ATR document flags (for minutes voting section) */
  @Input() meetingDocuments: any;
  /** Parent increments when user opens the tab to refetch */
  @Input() refreshKey = 0;

  loading = false;
  loadError: string | null = null;
  rows: AgendaVoteRow[] = [];
  minuteRows: MinuteVoteRow[] = [];

  detailAgenda: any;
  /** Set when opening detail for a minutes row */
  detailMinuteLabel: string | null = null;
  detailSummary: AgendaVoteRow['summary'];
  detailVoters: Array<{
    userName: string;
    userEmail: string;
    votedAt: Date | string | null;
    comments: string | null;
    voteType: 'APPROVE' | 'REJECT';
  }> = [];

  constructor(
    private meetingService: MeetingMasterService,
    private modal: NgbModal,
    private commonFunction: CommonFunction
  ) {}

  safeReadable(value: any): string {
    const raw = value == null ? '' : String(value);
    if (!raw) {
      return '';
    }
    try {
      const dec = this.commonFunction.decrypt(raw);
      if (dec && dec.trim()) {
        return dec.trim();
      }
    } catch {}
    return raw;
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['refreshKey'] && !ch['refreshKey'].firstChange) {
      this.loadAll();
      return;
    }
    if (ch['meetingId'] || ch['agendas'] || ch['meetingDocuments']) {
      if (this.meetingId && (this.agendas?.length || this.hasMinutesDocuments())) {
        this.loadAll();
      } else {
        this.rows = [];
        this.minuteRows = [];
      }
    }
  }

  hasMinutesDocuments(): boolean {
    const d = this.meetingDocuments;
    return !!(d?.draftFile?.isAvaible || d?.finalFile?.isAvaible);
  }

  loadAll(): void {
    if (!this.meetingId) {
      this.rows = [];
      this.minuteRows = [];
      return;
    }

    const hasAgendas = !!(this.agendas?.length);
    const hasMinutes = this.hasMinutesDocuments();

    if (!hasAgendas && !hasMinutes) {
      this.rows = [];
      this.minuteRows = [];
      return;
    }

    this.loading = true;
    this.loadError = null;

    const agendaObs =
      hasAgendas && this.agendas
        ? forkJoin(
            this.agendas
              .filter((a) => a && a._id)
              .map((agenda) =>
                this.meetingService.getAgendaVoteSummary(this.meetingId, agenda._id).pipe(
                  map((res: any) => {
                    const ok = res != null && res.status == 0;
                    if (!ok) {
                      return {
                        agenda,
                        summary: null,
                        errorMessage: res?.message || 'Unable to load votes for this agenda.',
                      } as AgendaVoteRow;
                    }
                    const d = res.data || {};
                    return {
                      agenda,
                      summary: {
                        approveCount: d.approveCount || 0,
                        rejectCount: d.rejectCount || 0,
                        totalVotes: d.totalVotes ?? (d.approveCount || 0) + (d.rejectCount || 0),
                        approveVoters: Array.isArray(d.approveVoters) ? d.approveVoters : [],
                        rejectVoters: Array.isArray(d.rejectVoters) ? d.rejectVoters : [],
                      },
                      errorMessage: null,
                    } as AgendaVoteRow;
                  }),
                  catchError(() =>
                    of({
                      agenda,
                      summary: null,
                      errorMessage: 'Network error while loading votes.',
                    } as AgendaVoteRow)
                  )
                )
              )
          )
        : of([] as AgendaVoteRow[]);

    const minuteDefs: { minuteType: 'DRAFT' | 'FINAL'; label: string }[] = [];
    const md = this.meetingDocuments;
    if (md?.draftFile?.isAvaible) {
      minuteDefs.push({ minuteType: 'DRAFT', label: 'Draft minutes' });
    }
    if (md?.finalFile?.isAvaible) {
      minuteDefs.push({ minuteType: 'FINAL', label: 'Final minutes' });
    }

    const minuteObs =
      minuteDefs.length > 0
        ? forkJoin(
            minuteDefs.map((def) =>
              this.meetingService.getMinuteVoteSummary(this.meetingId, def.minuteType).pipe(
                map((res: any) => {
                  const ok = res != null && res.status == 0;
                  if (!ok) {
                    return {
                      minuteType: def.minuteType,
                      label: def.label,
                      summary: null,
                      errorMessage: res?.message || 'Unable to load votes for these minutes.',
                    } as MinuteVoteRow;
                  }
                  const d = res.data || {};
                  return {
                    minuteType: def.minuteType,
                    label: def.label,
                    summary: {
                      approveCount: d.approveCount || 0,
                      rejectCount: d.rejectCount || 0,
                      totalVotes: d.totalVotes ?? (d.approveCount || 0) + (d.rejectCount || 0),
                      approveVoters: Array.isArray(d.approveVoters) ? d.approveVoters : [],
                      rejectVoters: Array.isArray(d.rejectVoters) ? d.rejectVoters : [],
                    },
                    errorMessage: null,
                  } as MinuteVoteRow;
                }),
                catchError(() =>
                  of({
                    minuteType: def.minuteType,
                    label: def.label,
                    summary: null,
                    errorMessage: 'Network error while loading votes.',
                  } as MinuteVoteRow)
                )
              )
            )
          )
        : of([] as MinuteVoteRow[]);

    forkJoin({ agendas: agendaObs, minutes: minuteObs }).subscribe({
      next: ({ agendas, minutes }) => {
        this.rows = agendas;
        this.minuteRows = minutes;
        this.loading = false;
      },
      error: () => {
        this.loadError = 'Could not load voting results.';
        this.loading = false;
      },
    });
  }

  openDetail(content: any, row: AgendaVoteRow): void {
    if (!row.summary) {
      return;
    }
    this.detailAgenda = row.agenda;
    this.detailMinuteLabel = null;
    this.detailSummary = row.summary;
    this.setDetailVotersFromSummary(row.summary);
    this.modal.open(content, { size: 'lg', scrollable: true, backdrop: true });
  }

  openMinuteDetail(content: any, row: MinuteVoteRow): void {
    if (!row.summary) {
      return;
    }
    this.detailAgenda = null;
    this.detailMinuteLabel = row.label;
    this.detailSummary = row.summary;
    this.setDetailVotersFromSummary(row.summary);
    this.modal.open(content, { size: 'lg', scrollable: true, backdrop: true });
  }

  private setDetailVotersFromSummary(summary: NonNullable<AgendaVoteRow['summary']>): void {
    const approve = (summary.approveVoters || []).map((v) => ({
      userName: v.userName || '—',
      userEmail: this.safeReadable(v.userEmail || ''),
      votedAt: v.votedAt,
      comments: this.safeReadable(v.comments || '') || null,
      voteType: 'APPROVE' as const,
    }));
    const reject = (summary.rejectVoters || []).map((v) => ({
      userName: v.userName || '—',
      userEmail: this.safeReadable(v.userEmail || ''),
      votedAt: v.votedAt,
      comments: this.safeReadable(v.comments || '') || null,
      voteType: 'REJECT' as const,
    }));
    this.detailVoters = [...approve, ...reject].sort((a, b) =>
      (a.userName || '').localeCompare(b.userName || '')
    );
  }

  initials(name: string): string {
    if (!name || typeof name !== 'string') {
      return '?';
    }
    const p = name.trim().split(/\s+/).filter(Boolean);
    if (p.length === 0) {
      return '?';
    }
    if (p.length === 1) {
      return p[0].charAt(0).toUpperCase();
    }
    return (p[0].charAt(0) + p[p.length - 1].charAt(0)).toUpperCase();
  }
}
